import {
  type RouteLocationNormalizedGeneric,
  type RouteRecordRaw,
} from "vue-router";

import { showLoadErrorPage } from "shared/boot/errors";
import { getLocaleText } from "shared/boot/i18n";
import {
  type InsightsDashboardProps,
  type InsightsLandingPageType,
} from "shared/modules/Insights/types";
import { useInsightsDashboardStore } from "shared/modules/InsightsDashboard/stores/useInsightsDashboardStore";
import { type Uuid } from "shared/types";

export type InsightsDashboardRouteName = "dashboard" | "dashboard-chart";

export interface InsightDashboardRoutesConfig {
  isCreate?: boolean;
  type: InsightsLandingPageType;
}

export function useInsightsDashboardRoutes() {
  function buildInsightsDashboardRouteProps(
    config: InsightDashboardRoutesConfig,
    route: RouteLocationNormalizedGeneric
  ): InsightsDashboardProps {
    return {
      id: !config.isCreate ? Number(route.params.id) : undefined,
    };
  }

  function buildInsightsDashboardRouteName(
    config: InsightDashboardRoutesConfig & { path?: InsightsDashboardRouteName }
  ): string {
    let formattedPath: string = `insights-${config.type}`;

    if (config.isCreate) {
      formattedPath += "-new";
    }

    if (config.path) {
      formattedPath += `-${config.path}`;
    }

    return formattedPath;
  }

  function buildInsightsDashboardRoutes(
    config: InsightDashboardRoutesConfig
  ): RouteRecordRaw {
    return {
      beforeEnter: () => {
        useInsightsDashboardStore().landingPageType = config.type;

        return true;
      },
      children: [
        {
          component: () =>
            import("shared/modules/InsightsDashboard/pages/InsightsDashboardEdit/InsightsDashboardEdit.vue").catch(
              showLoadErrorPage
            ),
          name: buildInsightsDashboardRouteName({
            ...config,
            path: "dashboard",
          }),
          path: "",
          props: (route) => buildInsightsDashboardRouteProps(config, route),
        },
        {
          component: () =>
            import("shared/modules/InsightsDashboard/pages/InsightsDashboardChartDetail/InsightsDashboardChartDetail.vue").catch(
              showLoadErrorPage
            ),
          meta: {
            hideInsightsSearch: true,
            title: (route: RouteLocationNormalizedGeneric) => {
              const { getChartLabelByUuid } = useInsightsDashboardStore();

              return getChartLabelByUuid(route.params.uuid as Uuid);
            },
          },
          name: buildInsightsDashboardRouteName({
            ...config,
            path: "dashboard-chart",
          }),
          path: "chart/:uuid",
          props: (route) => ({
            ...buildInsightsDashboardRouteProps(config, route),
            uuid: route.params.uuid,
          }),
        },
      ],
      component: () =>
        import("shared/modules/InsightsDashboard/pages/InsightsDashboard/InsightsDashboard.vue").catch(
          showLoadErrorPage
        ),
      meta: {
        hideInsightsSearch: true,
        title: () => {
          const { dashboard, isLoading } = useInsightsDashboardStore();

          if (isLoading) {
            return getLocaleText("global.loading");
          }

          if (dashboard?.name) {
            return dashboard.name;
          }

          if (config.isCreate) {
            return getLocaleText("routes.insights_dashboard_new");
          }

          return getLocaleText("routes.insights_dashboard");
        },
      },
      path: config.isCreate ? "new" : ":id",
      props: (route) => buildInsightsDashboardRouteProps(config, route),
    };
  }

  return {
    buildInsightsDashboardRouteName,
    buildInsightsDashboardRoutes,
  };
}

export default useInsightsDashboardRoutes;
